import {
  dateUtils,
  type Except,
  notEmpty,
  type ValidatedKey,
} from '@festina-life/core'
import { useField } from 'formik'

import { DatePicker } from '../../forms/datePicker/DatePicker'
import type { RawDatePickerProps } from '../../forms/datePicker/RawDatePicker'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useDateValueDifferenceCheck } from '../formikUtils/useDateValueDifferenceCheck'
import { withFormikInputDiffing } from '../formikUtils/withFormikInputDiffing'
import type { FormikInputPropsForWrapper } from '../types'

export type FormikDatePickerProps = Except<
  RawDatePickerProps,
  'defaultValue' | 'onChange' | 'onBlur'
> & {
  name: ValidatedKey
  defaultValue?: string
  existingValue?: string
  showExistingValue?: boolean
  onBlur?: (value: string | undefined) => void
} & FormikInputPropsForWrapper

export const FormikDatePickerInternal = ({
  name,
  isRequired,
  label,
  description,
  defaultValue,
  validationText,
  onDefaultValueClick,
  disabled,
  onBlur,
  hasRightColumnAlways,
  showExistingValue,
  existingValueConfig,
  className,
  granularity,
  ...restProps
}: FormikDatePickerProps) => {
  const [field, meta, { setValue }] = useField({ name })
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)
  const formatDate =
    granularity === 'minute' ? dateUtils.dateWithTimeAsStr : dateUtils.dateAsStr
  const existingValue = useExistingValuesContext()?.getExistingValue?.(
    name,
  ) as string

  const handleOnBlur = (newValue: string) => {
    // We can avoid creating a fake event here.
    // Formik's onBlur only needs to look at the target's name.
    field.onBlur({ target: { name } })
    onBlur?.(newValue)
  }

  const handleOnChange = (newValue: string) => {
    // We can avoid creating a fake event here.
    // Formik's onChange only needs to look at the target's name, value and type.
    // The specific type doesn't matter, as long as it is
    // something that won't cause Formik to modify the value.
    field.onChange({ target: { name, value: newValue, type: 'text' } })
  }

  const inputValueDifferenceFromExistingValue =
    useDateValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    label,
    existingValue: notEmpty(existingValue)
      ? formatDate(existingValue)
      : undefined,
    existingValueConfig,
    inputValueDifferenceFromExistingValue,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      validationText,
      defaultValue: notEmpty(defaultValue)
        ? formatDate(defaultValue)
        : undefined,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick:
            onDefaultValueClick ?? (() => setValue(defaultValue)),
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      hasRightColumnAlways,
      error,
      className,
    },
  })

  return (
    <DatePicker
      wrapperProps={wrapperProps}
      id={name}
      placeholder={
        (existingValue ?? defaultValue)
          ? formatDate(existingValue ?? defaultValue)
          : undefined
      }
      isInError={isInError}
      disabled={isFieldDisabled}
      granularity={granularity}
      {...field}
      {...restProps}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
    />
  )
}

export const FormikDatePicker = withFormikInputDiffing(FormikDatePickerInternal)
